// Pixel-art sprite actors standing in the 3D diorama (HD-2D). Each actor is a
// camera-facing quad anchored at its ground contact, sampled nearest, lit by a
// per-actor light colour, and tinted where it sits below the water line.
import * as THREE from 'three';

const vs = /* glsl */`
uniform vec4 frame; // u0, v0, du, dv
uniform float flip;
varying vec2 vUv; varying vec3 vWorld; varying float vLocalY;
void main() {
  vLocalY = position.y;
  vec2 uv = vec2(flip > 0.5 ? 1.0 - uv.x : uv.x, uv.y);
  vUv = frame.xy + uv * frame.zw;
  vec4 w = modelMatrix * vec4(position, 1.0);
  vWorld = w.xyz;
  gl_Position = projectionMatrix * viewMatrix * w;
}`;
const fs = /* glsl */`
uniform sampler2D map; uniform vec3 light; uniform float flash; uniform vec3 glow; uniform float opacity;
uniform float waterY; uniform vec3 waterTint; uniform float dim; uniform float wet;
varying vec2 vUv; varying vec3 vWorld; varying float vLocalY;
void main() {
  vec4 t = texture2D(map, vUv);
  if (t.a < 0.5) discard;
  vec3 c = t.rgb * light;
  // Pixels below the anchor row are the near half of the footprint: a touch of water.
  c = mix(c, c * waterTint, smoothstep(0.0, -0.25, vLocalY) * 0.35 * wet);
  c = mix(c, vec3(dot(c, vec3(0.33))), dim);
  c += glow + flash;
  if (opacity < 0.999 && fract(sin(dot(floor(gl_FragCoord.xy), vec2(12.9898, 78.233))) * 43758.5453) > opacity) discard;
  gl_FragColor = vec4(c, 1.0);
}`;

const shadowTex = (() => {
  const cv = document.createElement('canvas'); cv.width = cv.height = 32;
  const ctx = cv.getContext('2d');
  // Stepped (pixel) blob shadow.
  for (let y = 0; y < 32; y++) for (let x = 0; x < 32; x++) {
    const d = Math.hypot((x - 15.5) / 15.5, (y - 15.5) / 15.5);
    const a = d < 0.55 ? 0.42 : d < 0.8 ? 0.26 : d < 1 ? 0.1 : 0;
    ctx.fillStyle = `rgba(4,6,18,${a})`; ctx.fillRect(x, y, 1, 1);
  }
  const t = new THREE.CanvasTexture(cv); t.magFilter = t.minFilter = THREE.NearestFilter; return t;
})();

const ringTex = (() => {
  const cv = document.createElement('canvas'); cv.width = 32; cv.height = 16;
  const ctx = cv.getContext('2d');
  for (let y = 0; y < 16; y++) for (let x = 0; x < 32; x++) {
    const d = Math.hypot((x - 15.5) / 15.5, (y - 7.5) / 7.5);
    if (d > 0.78 && d < 1) { ctx.fillStyle = `rgba(190,235,255,${d > 0.9 ? 0.55 : 0.3})`; ctx.fillRect(x, y, 1, 1); }
  }
  const t = new THREE.CanvasTexture(cv); t.magFilter = t.minFilter = THREE.NearestFilter; return t;
})();

// Sprites are drawn from the gameplay camera's 55° view, so the image is laid
// on a camera-facing plane through the footprint centre and slid toward the
// camera far enough that its lowest pixels clear the water. Screen position
// does not change; only depth does.
const LIFT = 0.9;

export class SpriteBank {
  constructor() { this.sheets = {}; }

  async load(base = '/sprites/') {
    try {
      const res = await fetch(base + 'sprites.json');
      if (!res.ok) throw new Error(res.status);
      const meta = await res.json();
      const loader = new THREE.TextureLoader();
      await Promise.all(Object.entries(meta).map(async ([name, m]) => {
        const tex = await loader.loadAsync(base + name + '.png');
        this.add(name, m, tex);
      }));
      this.real = true;
    } catch (e) {
      console.info('sprites: using placeholders', e.message);
    }
    for (const name of ['goro', 'bou', 'chika', 'mio', 'fry', 'takoko', 'seahorse', 'fugu', 'anemone', 'nushi']) {
      if (!this.sheets[name]) this.add(name, ...placeholder(name));
    }
  }

  add(name, meta, tex) {
    tex.magFilter = THREE.NearestFilter; tex.minFilter = THREE.NearestFilter; tex.generateMipmaps = false;
    tex.colorSpace = THREE.SRGBColorSpace;
    const rows = Math.max(...Object.values(meta.anims).map(a => a.row)) + 1;
    const cols = Math.max(...Object.values(meta.anims).map(a => a.frames));
    this.sheets[name] = { meta, tex, w: tex.image.width, h: tex.image.height, rows, cols };
  }

  actor(name) { return new Actor(this.sheets[name]); }   // shell swaps and patterns show in the 3D look only
}

export class Actor {
  constructor(sheet) {
    this.sheet = sheet;
    const { meta, w, h } = sheet;
    const [cw, ch] = meta.cell, ppu = meta.ppu;
    const [ax, ay] = meta.anchor;
    const geo = new THREE.PlaneGeometry(cw / ppu, ch / ppu);
    geo.translate((cw / 2 - ax) / ppu, (ch / 2 - ay) / ppu * -1 + 0, 0);
    // anchor y counts from the top of the cell; move the anchor to the origin
    geo.translate(0, 0, 0);
    this.uniforms = {
      map: { value: sheet.tex }, frame: { value: new THREE.Vector4(0, 0, cw / w, ch / h) }, flip: { value: 0 },
      light: { value: new THREE.Color(1, 1, 1) }, flash: { value: 0 }, glow: { value: new THREE.Color(0, 0, 0) },
      opacity: { value: 1 }, waterY: { value: 0 }, wet: { value: 1 }, waterTint: { value: new THREE.Color(0.35, 0.62, 0.8) }, dim: { value: 0 },
    };
    this.mesh = new THREE.Mesh(geo, new THREE.ShaderMaterial({ vertexShader: vs, fragmentShader: fs, uniforms: this.uniforms }));
    this.mesh.renderOrder = 1;
    this.shadow = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, depthWrite: false }));
    this.shadow.rotation.x = -Math.PI / 2;
    this.shadow.renderOrder = 1;
    this.ring = new THREE.Mesh(new THREE.PlaneGeometry(1, 0.5), new THREE.MeshBasicMaterial({ map: ringTex, transparent: true, depthWrite: false }));
    this.ring.rotation.x = -Math.PI / 2;
    this.ring.renderOrder = 3;
    this.group = new THREE.Group();
    this.group.add(this.mesh, this.shadow, this.ring);
    this.anim = null; this.t = 0; this.frameIndex = 0;
    this.scale = new THREE.Vector2(1, 1);
    this.shadowSize = (cw / ppu) * 0.7;
    this.floorY = -0.3;
    this.play(Object.keys(meta.anims)[0]);
  }

  has(name) { return !!this.sheet.meta.anims[name]; }
  play(name, { restart = false, then = null } = {}) {
    if (!this.has(name)) return;
    if (this.anim === name && !restart) return;
    this.anim = name; this.t = 0; this.then = then;
    this.setFrame(0);
  }
  setFrame(k) {
    const { meta, w, h } = this.sheet;
    const a = meta.anims[this.anim];
    const [cw, ch] = meta.cell;
    this.frameIndex = Math.min(k, a.frames - 1);
    // Texture v runs bottom-up; rows count from the top of the sheet.
    this.uniforms.frame.value.set((this.frameIndex * cw) / w, 1 - ((a.row + 1) * ch) / h, cw / w, ch / h);
  }
  // Hold a specific frame (e.g. pull depth) without advancing.
  hold(name, k) { if (this.anim !== name) this.play(name); this.holding = true; this.setFrame(k); }
  release() { this.holding = false; }

  update(dt, camera) {
    const a = this.sheet.meta.anims[this.anim];
    if (a && !this.holding) {
      this.t += dt;
      const k = Math.floor(this.t * a.fps);
      if (a.loop === false && k >= a.frames) {
        this.setFrame(a.frames - 1);
        if (this.then) { const next = this.then; this.then = null; this.play(next); }
      } else this.setFrame(k % a.frames);
    }
    this.mesh.quaternion.copy(camera.quaternion);
    const toCam = new THREE.Vector3(0, 0, 1).applyQuaternion(camera.quaternion);
    this.mesh.position.copy(toCam).multiplyScalar(this.lift ?? LIFT);
    this.mesh.position.x += this.offsetX || 0;
    this.ring.position.set(0, 0.012 - this.group.position.y, 0);
    const rs = this.shadowSize * (1 + 0.06 * Math.sin(performance.now() / 300 + this.group.id));
    this.ring.scale.set(rs, rs, 1);
    this.ring.visible = this.wade !== false && Math.abs(this.group.position.y) < 0.2;
    const k = this.size ?? 1;
    this.mesh.scale.set(this.scale.x * k, this.scale.y * k, 1);
    this.shadow.position.set(0, this.floorY + 0.012 - this.group.position.y, 0);
    const s = this.shadowSize * (0.9 + 0.1 * this.scale.x);
    this.shadow.scale.set(s, s * 0.7, 1);
  }
  set flip(v) { this.uniforms.flip.value = v ? 1 : 0; }
  dispose() { this.mesh.geometry.dispose(); this.mesh.material.dispose(); this.shadow.geometry.dispose(); this.shadow.material.dispose(); }
}

// Placeholder sheets drawn on a canvas, same layout as the real ones, so the
// game runs before (or without) the rendered sprites.
function placeholder(name) {
  const crab = ['goro', 'bou', 'chika', 'mio'].includes(name);
  const big = name === 'nushi';
  const cw = big ? 160 : crab ? 48 : 32, ch = big ? 128 : crab ? 48 : 32;
  const anims = crab
    ? { idle: 4, pull: 3, spin: 8, land: 3, cheer: 4, stuck: 2, walk: 4 }
    : name === 'anemone' ? { idle: 4, hug: 3 }
    : big ? { idle: 4, ink: 3, calm: 4 }
    : { idle: 4, bump: 2, saved: 4, taken: 4, ...(name === 'fugu' ? { puff: 3 } : {}) };
  const names = Object.keys(anims);
  const cols = Math.max(...Object.values(anims));
  const cv = document.createElement('canvas');
  cv.width = cw * cols; cv.height = ch * names.length;
  const ctx = cv.getContext('2d');
  const colours = { goro: '#7a8a52', bou: '#e8b27a', chika: '#c8843a', mio: '#d9d2e6', fry: '#c9d6e8', takoko: '#f09ab0', seahorse: '#f2cf5a', fugu: '#e6d6a0', anemone: '#3fb4a0', nushi: '#7a1f2b' };
  const meta = { cell: [cw, ch], ppu: 40, anchor: [cw / 2, ch - 4], anims: {} };
  names.forEach((an, row) => {
    meta.anims[an] = { row, frames: anims[an], fps: an === 'spin' ? 16 : 6, loop: !['land', 'cheer', 'saved', 'taken', 'hug', 'puff', 'bump', 'calm', 'ink'].includes(an) };
    for (let k = 0; k < anims[an]; k++) {
      const ox = k * cw, oy = row * ch;
      const bob = an === 'idle' ? (k % 2) : 0;
      const r = (big ? 52 : crab ? 15 : 9) * (an === 'puff' ? 1 + k * 0.15 : 1);
      const cx = ox + cw / 2, cy = oy + ch - 4 - r - bob;
      ctx.fillStyle = '#10142a';
      ctx.beginPath(); ctx.arc(cx, cy, r + 1, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = colours[name];
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
      if (an === 'spin') { ctx.fillStyle = '#10142a'; ctx.fillRect(cx - 1 + Math.cos(k) * r * 0.6, cy - r, 2, r * 2); }
      if (crab && an !== 'spin' && !(an === 'pull' && k === 2)) {
        ctx.fillStyle = '#e0533a'; ctx.fillRect(cx - 9, cy + r - 6 - (an === 'pull' ? k * 3 : 0), 18, 6);
        ctx.fillStyle = '#fff'; ctx.fillRect(cx - 5, cy + r - 11, 3, 3); ctx.fillRect(cx + 2, cy + r - 11, 3, 3);
      }
      if (!crab) { ctx.fillStyle = '#fff'; ctx.fillRect(cx - 3, cy - 2, 2, 2); ctx.fillRect(cx + 2, cy - 2, 2, 2); }
    }
  });
  const tex = new THREE.CanvasTexture(cv);
  return [meta, tex];
}
